import React from 'react';
import { motion } from 'framer-motion';
type SectionFrameProps = {
  index: string;
  eyebrow: string;
  headline: string;
  subheadline: string;
  children: React.ReactNode;
};

/**
 * Every route is an independent narrative container: its own index, headline,
 * sub-headline, and warm foreground sheet over the structural canvas.
 */
export function SectionFrame({
  index,
  eyebrow,
  headline,
  subheadline,
  children
}: SectionFrameProps) {
  return <motion.section initial={{
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.28,
    ease: [0.23, 1, 0.32, 1]
  }} className="mx-auto w-full max-w-sheet px-5 py-12 sm:px-8 lg:py-16" aria-labelledby={`section-${index}`} data-id="element-123" data-mp="10lurdvk6g1r9">
      <header className="mb-10 border-b border-white/10 pb-8" data-id="element-124" data-mp="mug95qy3g4l9">
        <div className="flex items-baseline gap-4" data-id="element-125" data-mp="hulfjfmxp0wi">
          <span className="tabular display text-2xl font-black text-copper-light" data-id="element-126" data-mp="hta9ta9xhj2r">{index}</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-hydro-light" data-id="element-127" data-mp="cvqx4esvpecx">{eyebrow}</span>
        </div>
        <h1 id={`section-${index}`} className="display mt-4 max-w-5xl text-3xl font-black leading-[1.08] text-panel sm:text-4xl lg:text-[2.9rem]" data-id="element-128" data-mp="l1w408y88cpc">
          {headline}
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-panel/70 lg:text-lg" data-id="element-129" data-mp="6avnrhr8nein">{subheadline}</p>
      </header>
      {children}
    </motion.section>;
}
type PanelProps = {
  children: React.ReactNode;
  className?: string;
  tone?: 'warm' | 'steel';
};
export function Panel({
  children,
  className = '',
  tone = 'warm'
}: PanelProps) {
  const toneClass = tone === 'warm' ? 'bg-panel text-steel-dark border-panel-deep' : 'plate-steel text-panel border-white/10';
  return <div className={`rounded-sm border ${toneClass} ${className}`} data-id="element-130" data-mp="u0pztx3qgz1u">{children}</div>;
}
type QuoteProps = {
  children: React.ReactNode;
};

/** Focal quote — Shrikhand display weight, the loudest voice on the page. */
export function FocalQuote({
  children
}: QuoteProps) {
  return <figure className="mt-12 border-l-4 border-copper bg-steel-dark/80 px-6 py-8 sm:px-10" data-id="element-131" data-mp="1l8rkhkf65y60">
      <blockquote className="impact max-w-4xl text-xl text-panel sm:text-2xl lg:text-[1.75rem]" data-id="element-132" data-mp="1gcauslsbnbqt">
        “{children}”
      </blockquote>
      <figcaption className="mt-4 text-[11px] font-semibold uppercase tracking-[0.26em] text-hydro-light" data-id="element-133" data-mp="1bhwuc4ysyefa">
        David Lukcic, P.E.
      </figcaption>
    </figure>;
}
type MetricProps = {
  value: string;
  label: string;
  detail?: string;
};
export function Metric({
  value,
  label,
  detail
}: MetricProps) {
  return <div className="flex h-full flex-col border-t-2 border-copper bg-steel-dark/70 px-5 py-6" data-id="element-134" data-mp="sl5e64ek4ffg">
      <p className="tabular display text-3xl font-black leading-none text-panel lg:text-4xl" data-id="element-135" data-mp="nmapiltwrwnf">{value}</p>
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-copper-light" data-id="element-136" data-mp="2mz6b2jqjqscw">{label}</p>
      {detail ? <p className="mt-auto pt-3 text-sm leading-relaxed text-panel/65" data-id="element-137" data-mp="2i023fxj9t2oo">{detail}</p> : null}
    </div>;
}